import { useMemo } from 'react';
import type { Edge, Node, Rect, Viewport, XYPosition } from '@xyflow/react';
import { concentricLayout } from './concentricLayout';
import { nodeLabel, type ViewEdge, type ViewNode } from './graphModel';
import { MOVE_MS, motionDuration } from './motion';
import { useAnimatedPositions, type PositionMover } from './useAnimatedPositions';

type Size = { width: number; height: number };

/** Widths match the fixed `w-*` class of each node component; heights fit its tallest content. */
const NODE_SIZE: Record<ViewNode['kind'], Size> = {
  aircraft: { width: 160, height: 44 },
  // ESN line + "working" badge line + engine type line.
  engine: { width: 200, height: 84 },
  // Number + authority badge line, a status badge wrapped under them when the row is full, + title line.
  reference: { width: 200, height: 84 },
  document: { width: 240, height: 84 },
  // Title line + date line.
  event: { width: 200, height: 60 },
  // P/N, S/N, and description lines.
  part: { width: 200, height: 74 },
  // An IPC position draws the generic card (`w-40`): one label line.
  ipc: { width: 160, height: 44 },
  // A collapsed card with a two-line label (measured: 58); one line is 42, an expanded group adds 18.
  group: { width: 220, height: 58 },
  project: { width: 200, height: 56 },
};
const FALLBACK_SIZE = { width: 160, height: 44 };
export const nodeSize = (kind: string): Size => NODE_SIZE[kind as keyof typeof NODE_SIZE] ?? FALLBACK_SIZE;

/**
 * The React Flow node type of a kind. React Flow styles its built-in `group` type (a bordered box), so the
 * group kind gets its own name; kinds without a `NODE_SIZE` entry are `generic`.
 */
const flowType = (kind: string) =>
  kind === 'group' ? 'drillGroup' : NODE_SIZE[kind as keyof typeof NODE_SIZE] ? kind : 'generic';

export type FlowNodeData = {
  node: ViewNode;
  selected: boolean;
  /** The root of the view. */
  focused: boolean;
  /** The node shows its children: it is the root or in `expandedIds`. */
  expanded: boolean;
  /** Expanding the node would show children (see `VisibleGraph.expandableIds`). */
  expandable: boolean;
  /** Set when the node can collapse: it is expanded and not the root. */
  onCollapse?: (id: string) => void;
  /** Set on an expanded group with members left to show (`remaining`): shows GROUP_PAGE more. */
  onShowMore?: (id: string) => void;
  /** Entity cards only: the documents that mention the node, as the sidebar counts them (see `cardMentions`). */
  mentions?: number;
};

const ENTITY_KINDS: ReadonlySet<string> = new Set(['engine', 'aircraft', 'part', 'reference', 'event']);

/**
 * The mention count an entity card shows: the node's entry in `mentionCounts`, the number behind the sidebar's
 * "Mentioned by N documents". `undefined` for a document, group, or project card, which shows none.
 */
export const cardMentions = (node: ViewNode, counts: ReadonlyMap<string, number>): number | undefined =>
  ENTITY_KINDS.has(node.kind) ? (counts.get(node.id) ?? 0) : undefined;

/** The chevron props of a real node's `NodeShell`. */
export const chevronProps = (data: FlowNodeData) => ({
  expandable: data.expandable,
  onCollapse: data.onCollapse ? () => data.onCollapse?.(data.node.id) : undefined,
});

/** The name a screen reader gives the node. */
export function viewNodeLabel(node: ViewNode): string {
  if (node.kind === 'group') return `${node.label}, ${node.total.toLocaleString('en-US')} items`;
  if (node.kind === 'project') return node.label;
  return nodeLabel(node);
}

const EDGE_CLASS = {
  subject: '[--xy-edge-stroke:var(--color-indigo-500)] dark:[--xy-edge-stroke:var(--color-indigo-400)]',
  mention: '[--xy-edge-stroke:var(--color-zinc-400)] dark:[--xy-edge-stroke:var(--color-zinc-600)]',
  // Grouping edges are dashed, so they read as structure rather than as a mention. Only the drawn path is dashed:
  // React Flow's invisible interaction path keeps a solid hit area. Tailwind reads `_` as a space and `\_` as an
  // underscore, so the raw string spells the class `react-flow__edge-path`.
  synthetic: [
    '[--xy-edge-stroke:var(--color-zinc-300)] dark:[--xy-edge-stroke:var(--color-zinc-600)]',
    String.raw`[&_.react-flow\_\_edge-path]:[stroke-dasharray:6_4]`,
  ].join(' '),
  // Structural edges (installed-on, applies-to) are dashed with more contrast than a mention, so they read as a relation.
  structural: [
    '[--xy-edge-stroke:var(--color-zinc-500)] dark:[--xy-edge-stroke:var(--color-zinc-400)]',
    String.raw`[&_.react-flow\_\_edge-path]:[stroke-dasharray:4_3]`,
  ].join(' '),
  // A real edge that places no node (not in `VisibleGraph.treeEdges`): its role's colour, faded, so the tree reads
  // first. Adds no dash: the dash marks a grouping or structural edge.
  offTree: 'opacity-40',
  // The selection's path to the root: solid indigo at 2px, whatever the edge's role.
  onPath: [
    '[--xy-edge-stroke:var(--color-indigo-600)] dark:[--xy-edge-stroke:var(--color-indigo-300)]',
    '[--xy-edge-stroke-width:2]',
  ].join(' '),
  // An edge of the hovered or focused node: 2px on top of its role's stroke.
  incident: '[--xy-edge-stroke-width:2]',
  // Any other tree edge while a node is hovered: dimmed a little, so that node's edges stand out and the rest
  // still read.
  notIncident: 'opacity-60',
};

/** An edge's stroke by role alone: its colour, and the dash of a grouping or structural edge. */
const roleClass = (e: ViewEdge) =>
  e.synthetic
    ? EDGE_CLASS.synthetic
    : e.kind && e.kind !== 'mentions'
      ? EDGE_CLASS.structural
      : EDGE_CLASS[e.role ?? 'mention'];

/** An edge's stroke by role; grouping edges are dashed, and a real edge that is not a tree edge is faded. */
export function edgeClass(e: ViewEdge, inTree: boolean): string {
  const role = roleClass(e);
  return e.synthetic || inTree ? role : `${role} ${EDGE_CLASS.offTree}`;
}

/** The stroke for an edge of the hovered or focused node: its role's, at 2px, never faded. */
export const incidentEdgeClass = (e: ViewEdge): string => `${roleClass(e)} ${EDGE_CLASS.incident}`;

/** A drawn edge's data: the class it takes when an end of it is hovered or focused (see `edgesToDraw`). */
export type FlowEdgeData = { incidentClass: string };
export type FlowEdge = Edge<FlowEdgeData>;

/** The stroke for an edge on the selection's path to the root, whatever the edge's role. */
export const PATH_EDGE_CLASS = EDGE_CLASS.onPath;

/**
 * React Flow nodes and edges for the positioned view; `data` carries the view node only. `treeEdgeIds` are the
 * edges that place a node (`VisibleGraph.treeEdges`); every other real edge is drawn faded.
 */
export function toFlow(
  nodes: ViewNode[],
  edges: ViewEdge[],
  positions: ReadonlyMap<string, XYPosition>,
  treeEdgeIds: ReadonlySet<string>,
): { positioned: Node<FlowNodeData>[]; flowEdges: FlowEdge[] } {
  const positioned: Node<FlowNodeData>[] = nodes.flatMap((n) => {
    const position = positions.get(n.id);
    return position
      ? [
          {
            id: n.id,
            type: flowType(n.kind),
            position,
            // Until the canvas has a measured size for the card, the minimap and the fits size it from here.
            initialWidth: nodeSize(n.kind).width,
            initialHeight: nodeSize(n.kind).height,
            ariaLabel: viewNodeLabel(n),
            data: { node: n, selected: false, focused: false, expanded: false, expandable: false },
          },
        ]
      : [];
  });
  const flowEdges: FlowEdge[] = edges
    .filter((e) => positions.has(e.from) && positions.has(e.to))
    .map((e) => ({
      id: e.id,
      source: e.from,
      target: e.to,
      // Handles sit at the node centres, so a straight line runs centre to centre under the cards.
      type: 'straight',
      className: edgeClass(e, treeEdgeIds.has(e.id)),
      data: { incidentClass: incidentEdgeClass(e) },
    }));
  return { positioned, flowEdges };
}

/**
 * The edges the canvas draws. At overview zoom (below `COMPACT_ZOOM`) only the tree edges and the selection's
 * path: the rest would be a hairball of lines under blocks too small to read. The edges of the hovered or focused
 * node (`hoveredId`) take their role's colour at 2px and every other tree edge dims a little. The path is drawn
 * emphasised and last, so no other edge crosses over it. The hovered node's edges keep their place in the order:
 * a click leaves the pointer on the card it moves, and moving its many edges (a group's members) to the end
 * delayed the first frame of the move enough to shift an anchored card. `flowEdges` itself when nothing is left
 * out or restyled.
 */
export function edgesToDraw(
  flowEdges: FlowEdge[],
  treeEdgeIds: ReadonlySet<string>,
  pathEdgeIds: ReadonlySet<string>,
  overview: boolean,
  hoveredId: string | null = null,
): FlowEdge[] {
  const kept = overview ? flowEdges.filter((e) => treeEdgeIds.has(e.id) || pathEdgeIds.has(e.id)) : flowEdges;
  const incident = (e: FlowEdge) => e.source === hoveredId || e.target === hoveredId;
  // A hovered node none of whose edges is drawn emphasises nothing, so nothing dims.
  const hovered = hoveredId !== null && kept.some((e) => !pathEdgeIds.has(e.id) && incident(e));
  if (pathEdgeIds.size === 0 && !hovered) return kept;
  const rest: FlowEdge[] = [];
  const onPath: FlowEdge[] = [];
  for (const e of kept) {
    if (pathEdgeIds.has(e.id)) onPath.push({ ...e, className: PATH_EDGE_CLASS });
    else if (hovered && incident(e)) rest.push({ ...e, className: e.data?.incidentClass ?? e.className });
    // A non-tree edge is already fainter than the dim.
    else if (hovered && treeEdgeIds.has(e.id))
      rest.push({ ...e, className: `${e.className} ${EDGE_CLASS.notIncident}` });
    else rest.push(e);
  }
  return [...rest, ...onPath];
}

/** The node and every node a drawn edge joins it to: its ring 1 when it is the layout centre. */
export const ringOneIds = (nodeId: string, edges: readonly Edge[]): string[] => [
  nodeId,
  ...edges.flatMap((e) => (e.source === nodeId ? [e.target] : e.target === nodeId ? [e.source] : [])),
];

/** The box around the layout slots of `nodeIds` (every node when `null`); `null` when none is laid out. */
export function targetBounds(
  targets: readonly Node<FlowNodeData>[],
  nodeIds: readonly string[] | null,
): { x: number; y: number; width: number; height: number } | null {
  const ids = nodeIds && new Set(nodeIds);
  const slots = targets.filter((n) => !ids || ids.has(n.id));
  if (slots.length === 0) return null;
  const left = Math.min(...slots.map((n) => n.position.x));
  const top = Math.min(...slots.map((n) => n.position.y));
  const right = Math.max(...slots.map((n) => n.position.x + (n.initialWidth ?? 0)));
  const bottom = Math.max(...slots.map((n) => n.position.y + (n.initialHeight ?? 0)));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * `box` grown on its nearer side along each axis until `point` is its centre, so a fit of the box puts `point`
 * at the centre of the view. A partial ring (one neighbour above) would otherwise pull the selected card off it.
 */
export function centredOn(box: Rect, point: XYPosition): Rect {
  const halfWidth = Math.max(point.x - box.x, box.x + box.width - point.x);
  const halfHeight = Math.max(point.y - box.y, box.y + box.height - point.y);
  return { x: point.x - halfWidth, y: point.y - halfHeight, width: 2 * halfWidth, height: 2 * halfHeight };
}

/**
 * The viewport that keeps a node where it was on screen, at the same zoom, after a new layout moved it: the
 * first of `nodeIds` placed in both layouts is the anchor. `null` when none is.
 */
export function anchoredViewport(
  viewport: Viewport,
  before: ReadonlyMap<string, XYPosition>,
  after: ReadonlyMap<string, XYPosition>,
  nodeIds: readonly string[],
): Viewport | null {
  for (const id of nodeIds) {
    const from = before.get(id);
    const to = after.get(id);
    // A screen point is the flow point times the zoom plus the viewport offset.
    if (from && to)
      return {
        x: viewport.x + (from.x - to.x) * viewport.zoom,
        y: viewport.y + (from.y - to.y) * viewport.zoom,
        zoom: viewport.zoom,
      };
  }
  return null;
}

/**
 * Above this many visible nodes a move is instant. Each frame of a move redraws every node's position (the cards
 * themselves are memoised): timed on the big fixture (headless Chromium, dev build), a frame took 50-83 ms at 115
 * nodes and 100-150 ms at 180, and at 275-292 the render that starts the move already outlasts it, so no frame
 * showed motion.
 */
export const MOVE_ANIMATION_MAX_NODES = 200;

/** How long a new layout's move takes: `MOVE_MS`, or 0 under reduced motion or above `MOVE_ANIMATION_MAX_NODES`. */
export const moveDuration = (nodeCount: number, reduced?: boolean) =>
  nodeCount > MOVE_ANIMATION_MAX_NODES ? 0 : motionDuration(MOVE_MS, reduced);

/** The identity of a layout: the centre and the visible node and edge ids, the only inputs the rings read. */
export const layoutKeyOf = (nodes: readonly ViewNode[], edges: readonly ViewEdge[], centreId: string) =>
  centreId + '\u0003' + nodes.map((n) => n.id).join('\u0001') + '\u0002' + edges.map((e) => e.id).join('\u0001');

/**
 * Positions the visible nodes in rings around `centreId`: the ring of a node is its hop distance from the centre
 * over every visible edge, so each node the centre links to sits on ring 1. The rings run only when the centre or
 * the visible node or edge id set changes, and synchronously, so an expansion never shows a "laying out" frame.
 * `data` carries the view node only: the canvas overlays selection and expansion so those never trigger a
 * re-layout. A new layout moves the nodes to their new places over `moveMs` (see `useAnimatedPositions`).
 */
export function useGraphLayout(
  nodes: ViewNode[],
  edges: ViewEdge[],
  treeEdgeIds: ReadonlySet<string>,
  centreId: string,
): {
  /** The nodes where they are drawn now: on the way to their place in a new layout, then there. */
  positioned: Node<FlowNodeData>[];
  /** The nodes at their places in the layout, where a move ends. */
  targets: Node<FlowNodeData>[];
  flowEdges: FlowEdge[];
  error: boolean;
  layoutKey: string;
  /** The top-left corner of each laid-out node; a new map only with a new layout. */
  positions: ReadonlyMap<string, XYPosition>;
  /** How long this layout's move takes; a view move in step with it takes as long. */
  moveMs: number;
  /** Where the nodes are drawn, and where they were when this layout came. */
  mover: PositionMover;
} {
  const key = layoutKeyOf(nodes, edges, centreId);

  const rings = useMemo(() => {
    try {
      return { positions: concentricLayout(nodes, edges, centreId, nodeSize), failed: false };
    } catch (error: unknown) {
      console.error('Project graph layout failed', error);
      return { positions: new Map<string, XYPosition>(), failed: true };
    }
    // `key` captures the centre and node and edge identity; the layout reads nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const flow = useMemo(
    () => toFlow(nodes, edges, rings.positions, treeEdgeIds),
    [nodes, edges, rings, treeEdgeIds],
  );
  const moveMs = moveDuration(nodes.length);
  const { positions: drawn, mover } = useAnimatedPositions(rings.positions, moveMs);
  const positioned = useMemo(
    () =>
      drawn === rings.positions
        ? flow.positioned
        : flow.positioned.map((n) => ({ ...n, position: drawn.get(n.id) ?? n.position })),
    [flow, drawn, rings],
  );
  return {
    positioned,
    targets: flow.positioned,
    flowEdges: flow.flowEdges,
    error: rings.failed,
    layoutKey: key,
    positions: rings.positions,
    moveMs,
    mover,
  };
}
